import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { useUser } from '../user/UserContext';
import { useNavigate } from 'react-router-dom';
import RowDelete from '../ui/RowDelete';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Check } from 'lucide-react';

const ADMIN_ACTIVE_ORG_STORAGE_KEY = 'aimdocAdminActiveOrganizationId';

interface User {
    id: string;
    email: string;
    name: string;
}

interface Organization {
    id: string;
    name: string;
    users?: User[];
    license_type: string;
    trial_end_date: string | null;
    settings: any;
    operator: boolean;
}

interface OrganizationsTabProps {
    onShowUsersModal: (org: Organization) => void;
}

const OrganizationsTab: React.FC<OrganizationsTabProps> = ({ 
    onShowUsersModal,
}) => {
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [organizationSearchTerm, setOrganizationSearchTerm] = useState('');
    const [newOrganizationName, setNewOrganizationName] = useState('');
    const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isRb2bDomainsModalOpen, setIsRb2bDomainsModalOpen] = useState(false);
    const [isRb2bDomainsLoading, setIsRb2bDomainsLoading] = useState(false);
    const [rb2bOemDomains, setRb2bOemDomains] = useState<string[]>([]);
    const [activeAdminOrgId, setActiveAdminOrgId] = useState<string | null>(() => (
        localStorage.getItem(ADMIN_ACTIVE_ORG_STORAGE_KEY)
    ));
    const { get, post, put, remove } = useApi();
    const { organization, setOrg, user, refresh } = useUser();
    const canSwitchOrganization = Boolean(user?.is_admin);

    const getOrganizations = async () => {
        try {
            const response = await get('/admin/organizations/');
            setOrganizations(response.data || []);
        } catch (error) {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to load organizations');
            // Set safe default data on error
            setOrganizations([]);
        }
    };

    const persistAdminActiveOrganization = async (organizationId: string | null) => {
        await put('/users/me/admin-active-organization', {
            organization_id: organizationId,
        });
    };

    const handleOrgChange = async (newOrg: Organization) => {
        if (!canSwitchOrganization) {
            toast.error('Only global admins can switch active organization context.');
            return;
        }

        try {
            await persistAdminActiveOrganization(newOrg.id);
        } catch (error) {
            console.error('Error persisting admin active organization:', error);
            toast.error('Failed to switch organization context');
            return;
        }

        localStorage.setItem(ADMIN_ACTIVE_ORG_STORAGE_KEY, newOrg.id);
        setActiveAdminOrgId(newOrg.id);
        setOrg(newOrg);
        await refresh();
        navigate('/');
    };

    const handleClearOrgContext = async () => {
        try {
            await persistAdminActiveOrganization(null);
        } catch (error) {
            console.error('Error clearing admin active organization:', error);
            toast.error('Failed to clear organization context');
            return;
        }

        localStorage.removeItem(ADMIN_ACTIVE_ORG_STORAGE_KEY);
        setActiveAdminOrgId(null);
        await refresh();
        navigate('/');
    };

    const handleDeleteOrg = async (orgId: string) => {
        try {
            await remove(`/admin/organizations/${orgId}`);
            toast.success('Organization deleted successfully');
            getOrganizations();
        } catch (error) {
            console.error('Error deleting organization:', error);
            toast.error('Failed to delete organization');
        }
    };

    const handleCreateOrganization = async () => {
        const trimmedName = newOrganizationName.trim();
        if (!trimmedName) {
            toast.error('Organization name is required');
            return;
        }

        setIsCreatingOrganization(true);
        try {
            await post('/admin/organizations/', { name: trimmedName });
            toast.success('Organization created successfully');
            setNewOrganizationName('');
            await getOrganizations();
            setIsCreateModalOpen(false);
        } catch (error: any) {
            console.error('Error creating organization:', error);
            toast.error(error?.detail || 'Failed to create organization');
        } finally {
            setIsCreatingOrganization(false);
        }
    };

    const loadRb2bOemDomains = async () => {
        setIsRb2bDomainsLoading(true);
        try {
            const response = await get('/admin/rb2b-oem/domains');
            const payload = response.data || {};
            setRb2bOemDomains(Array.isArray(payload.domains) ? payload.domains : []);
        } catch (error) {
            console.error('Error loading RB2B OEM domains:', error);
            toast.error('Failed to load RB2B OEM domains');
            setRb2bOemDomains([]);
        } finally {
            setIsRb2bDomainsLoading(false);
        }
    };

    const handleOpenRb2bDomainsModal = () => {
        setIsRb2bDomainsModalOpen(true);
        loadRb2bOemDomains();
    };

    useEffect(() => {
        getOrganizations();
    }, []);

    const filteredOrgs = (organizations || []).filter(org =>
        org.name.toLowerCase().includes(organizationSearchTerm.toLowerCase())
    );

    const isIdentificationEnabled = (org: Organization) => {
        const settingsObj = org.settings && typeof org.settings === 'object' ? org.settings : {};
        const rb2bOemConfig =
            settingsObj && typeof settingsObj.rb2b_oem === 'object' ? settingsObj.rb2b_oem : {};
        const domains = Array.isArray(rb2bOemConfig.domains) ? rb2bOemConfig.domains : [];
        return Boolean(rb2bOemConfig.enabled) && domains.length > 0;
    };

    return (
        <TabsContent value="organizations" className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                    Total organizations: {organizations.length}
                </p>
                <div className="flex items-center gap-2">
                    {canSwitchOrganization && activeAdminOrgId && (
                        <Button variant="secondary" onClick={handleClearOrgContext}>
                            Clear Active Org
                        </Button>
                    )}
                    <Button variant="outline" onClick={handleOpenRb2bDomainsModal}>
                        View RB2B OEM Domains
                    </Button>
                    <Button onClick={() => setIsCreateModalOpen(true)}>
                        Create Organization
                    </Button>
                </div>
            </div>
            <Input
                type="text"
                placeholder="Search organizations..."
                value={organizationSearchTerm}
                onChange={(e) => setOrganizationSearchTerm(e.target.value)}
                className="mb-4"
            />
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[100px]">Select</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Org ID</TableHead>
                            <TableHead>License Type</TableHead>
                            <TableHead>Trial End Date</TableHead>
                            <TableHead>Identification Enabled</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredOrgs.map((org) => (
                            <TableRow key={org.id}>
                                <TableCell>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleOrgChange(org)}
                                        disabled={!canSwitchOrganization || organization?.id === org.id}
                                        className={organization?.id === org.id ? 'bg-accent' : ''}
                                    >
                                        {organization?.id === org.id ? <Check className="h-4 w-4" /> : 'Select'}
                                    </Button>
                                </TableCell>
                                <TableCell>{org.name}</TableCell>
                                <TableCell className="font-mono text-xs">{org.id}</TableCell>
                                <TableCell>{org.license_type}</TableCell>
                                <TableCell>{org.trial_end_date ? new Date(org.trial_end_date).toLocaleDateString() : 'N/A'}</TableCell>
                                <TableCell>{isIdentificationEnabled(org) ? 'Yes' : 'No'}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end space-x-2">
                                        <Button
                                            variant="ghost"
                                            onClick={() => onShowUsersModal(org)}
                                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                        >
                                            View Users
                                        </Button>
                                        <Button 
                                            variant="ghost"
                                            onClick={() => navigate(`/admin/organizations/${org.id}`)}
                                        >
                                            Edit
                                        </Button>
                                        <RowDelete
                                            deleteCallback={() => handleDeleteOrg(org.id)}
                                            deleteMessage={`Are you sure you want to delete ${org.name}?`}
                                        />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create organization</DialogTitle>
                        <DialogDescription>
                            Enter a name for the new organization.
                        </DialogDescription>
                    </DialogHeader>

                    <Input
                        type="text"
                        placeholder="Organization name"
                        value={newOrganizationName}
                        onChange={(e) => setNewOrganizationName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleCreateOrganization();
                            }
                        }}
                    />

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreateOrganization} disabled={isCreatingOrganization}>
                            {isCreatingOrganization ? 'Creating...' : 'Create Organization'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isRb2bDomainsModalOpen} onOpenChange={setIsRb2bDomainsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>RB2B OEM Domains</DialogTitle>
                        <DialogDescription>
                            Domains currently configured in the RB2B OEM instance.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-80 overflow-y-auto rounded-md border p-3">
                        {isRb2bDomainsLoading ? (
                            <p className="text-sm text-muted-foreground">Loading domains...</p>
                        ) : rb2bOemDomains.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No domains configured.</p>
                        ) : (
                            <div className="space-y-1">
                                {rb2bOemDomains.map((domain) => (
                                    <div key={domain} className="font-mono text-sm">
                                        {domain}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setIsRb2bDomainsModalOpen(false)}>
                            Close
                        </Button>
                        <Button
                            variant="outline"
                            onClick={loadRb2bOemDomains}
                            disabled={isRb2bDomainsLoading}
                        >
                            Refresh
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </TabsContent>
    );
};

export default OrganizationsTab;